import { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import {
  Button,
  Chip,
  Link as MUILink,
  Paper,
  Typography,
} from '@mui/material';
import { Stack } from '@mui/system';

import WaveUIAccordion from '@/shared/components/widgets/WaveUIAccordion';
import { AppPath } from '@/shared/constants/path';
import Payment from '@/shared/types/order/payment.type';
import formatCurrency from '@/shared/utils/formatCurrency';
import formatDate from '@/shared/utils/formatDate';

import WaveUINoDataOverlay from '../utilities/WaveUINoDataOverlay';

type CreateModeProps = {
  mode: 'create';
  onCreatePayment: () => void;
};

type ViewModeProps = {
  mode: 'view';
  onCreatePayment?: never;
};

type PaymentsDetailsPanelProps = (CreateModeProps | ViewModeProps) & {
  title: ReactNode;
  payments: Payment[];
};

const PaymentsDetailsPanel: FC<PaymentsDetailsPanelProps> = ({
  title,
  payments,
  mode,
  onCreatePayment,
}) => {
  const { t } = useTranslation(['payments', 'common']);

  return (
    <WaveUIAccordion title={title} defaultExpanded>
      {payments.length === 0 ? (
        <WaveUINoDataOverlay title={t('payments:noPayments')} size='small' />
      ) : (
        <Stack
          spacing={1}
          sx={{
            maxHeight: '40vh',
            overflowY: 'auto',
            mx: -1,
            px: 1,
            pb: 2,
          }}
        >
          {payments.map((payment) => (
            <Paper key={payment.in_transaction_id} elevation={2} sx={{ p: 2 }}>
              <Stack
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'auto 1fr auto',
                  gap: 2,
                  alignItems: 'center',
                }}
              >
                <Stack>
                  <MUILink
                    component={Link}
                    to={AppPath.Payments + '/' + payment.in_transaction_id}
                    underline='none'
                  >
                    {payment.in_transaction_id.split('-')[0]}
                  </MUILink>

                  <Typography variant='body2'>
                    {formatDate(payment.ts_created)}
                  </Typography>
                </Stack>

                <Stack direction='row' spacing={1}>
                  {payment.status && (
                    <Chip label={payment.status} size='small' />
                  )}

                  {payment.transaction_type && (
                    <Chip label={payment.transaction_type} size='small' />
                  )}
                </Stack>

                <Typography variant='body1'>
                  {formatCurrency(parseFloat(payment.transaction_amount))}
                </Typography>
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}

      {mode === 'create' ? (
        <Button onClick={onCreatePayment} variant='contained'>
          {t('payments:createPayment')}
        </Button>
      ) : (
        <Link to={AppPath.Payments}>
          <Button variant='contained' fullWidth>
            {t('common:viewAll')}
          </Button>
        </Link>
      )}
    </WaveUIAccordion>
  );
};

export default PaymentsDetailsPanel;
